import { makeStyles } from '@material-ui/core/styles';
import { Alert, Box, Button, CircularProgress, Col, Link, Paper, Row, Select, TextField, Typography, WorkflowViewContainer } from 'components/AORedesign';
import { Feature } from 'components/Feature';

import featureFlags from 'constants/featureFlags';
import PropTypes from 'prop-types';

import React, { useState } from 'react';

import { FormControl, Modal } from '@material-ui/core';
import axios from 'axios';
import { SelectStates } from 'components/AORedesign/SelectStates';

import { ADD_AGENT, DELETE_AGENT, UPDATE_AGENT } from 'constants/api';
import { handleOnChange } from '../../CommonUtilityServices/EventHandlerService';

const ADD_AGENT_LABEL = 'Add Agent';
const EDIT_AGENT_LABEL = 'Edit Agent';
const defaultFormData = {
  code: '',
  locname: '',
  city: '',
  state: '',
  county: '',
  address: '',
  postal: '',
  phone: '',
  web: '',
  fax: '',
  email: '',
  agent_type: '',
  lng: '',
  lat: '',
};
const AGENCY_TYPE = [
  { label: 'All lines', value: 'All lines' },
  { label: 'Bonds only', value: 'Bonds only' },
];

const useStyles = makeStyles({
  button: {
    margin: '10px',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
});

const AddOrUpdateAgent = ({ triggerButton, agent: initialAgent, resetSearchResults }) => {
  const classes = useStyles();

  const [agent, setAgent] = useState(initialAgent);
  const [formData, setFormData] = useState(agent ? { ...agent } : defaultFormData);
  const [isRequestFailed, setisRequestFailed] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // if agent details are not null then show edit agent otherwise add agent label
  const appBarTitleLabel = agent ? EDIT_AGENT_LABEL : ADD_AGENT_LABEL;

  const handleSubmitClick = () => {
    setLoading(true);
    setStatusMessage(null);
    const httpMethodName = agent ? 'put' : 'post';
    const agentUrl = agent ? `${UPDATE_AGENT}` : `${ADD_AGENT}`;
    axios({ method: httpMethodName, url: agentUrl, data: formData })
      .then((response) => {
        setLoading(false);
        if (response.status === 200) {
          setStatusMessage('Agent details submitted successfully.');
          setisRequestFailed(false);
        } else if (response.status === 409) {
          setStatusMessage('Agent details already exist. Please update the existing agent details by searching for the agent by code or name.');
          setisRequestFailed(true);
        } else {
          setStatusMessage('An error occurred while submitting agent details. Please try again.');
          setisRequestFailed(true);
        }
      })
      .catch((error) => {
        console.log(error);
        if (error.status === 409) {
          setStatusMessage('Agent details already exist. Please update the existing agent details by searching for the agent by code or name.');
          setisRequestFailed(true);
        } else {
          setStatusMessage('An error occurred while submitting agent details. Please try again.');
          setisRequestFailed(true);
        }
        setLoading(false);
      });
  };

  const handleClearClick = () => {
    if (agent) {
      setFormData({ ...agent });
    } else {
      setFormData(defaultFormData);
    }
    setisRequestFailed(false);
    setStatusMessage(null);
  };

  const handleResetOnClose = () => {
    if (agent) {
      setFormData({ ...agent });
    } else {
      setFormData(defaultFormData);
    }
    setisRequestFailed(false);
    setStatusMessage(null);
    //makes a call to search api to get the latest results
    if (typeof resetSearchResults === 'function') {
      resetSearchResults();
    }
  };

  const handleDeleteClick = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleOkClick = () => {
    setLoading(true);
    setisRequestFailed(false);
    setStatusMessage(null);
    axios
      .delete(`${DELETE_AGENT}/${agent.code}`)
      .then((response) => {
        if (response.status === 200) {
          setFormData(defaultFormData);
          /* 
          Set agent to null to trigger re-render so that it updates the label
           */
          setAgent(null);
          setLoading(false);
          setStatusMessage('Agent successfully deleted.');
        } else if (response.status === 404) {
          setStatusMessage('Agent does not exist.');
          setFormData(defaultFormData);
          setLoading(false);
        } else {
          setStatusMessage('Failed to delete the agent. Please try again.');
          setisRequestFailed(true);
          setLoading(false);
        }
        setIsModalOpen(false);
      })
      .catch(() => {
        setStatusMessage('Failed to delete the agent. Please try again.');
        setisRequestFailed(true);
        setLoading(false);
        setIsModalOpen(false);
      });
  };

  return (
    <Feature allowed={featureFlags.ACCESS_FIND_AGENT}>
      <WorkflowViewContainer appBarTitle={appBarTitleLabel} TriggerButton={triggerButton} handleResetOnClose={handleResetOnClose}>
        <Box mx={30} my={5}>
          <Typography variant="h5">Contact Info.</Typography>
          <Box p={1} />
          {statusMessage && <Alert severity={isRequestFailed ? 'error' : 'success'} message={statusMessage} />}
          <Paper variant="outlined" square style={{ boxSizing: 'border-box', height: '100%', width: '100%' }}>
            <Row container spacing={3}>
              <Col item xs={6}>
                <TextField
                  data-testid="agent-code"
                  fullWidth
                  label="Agent Code"
                  name="code"
                  required
                  value={formData?.code}
                  onChange={(e) => handleOnChange(e, formData, setFormData)}
                />
              </Col>
              <Col item xs={6}>
                <TextField fullWidth label="ZIP" name="postal" value={formData?.postal} onChange={(e) => handleOnChange(e, formData, setFormData)} required />
              </Col>
              <Col item xs={6}>
                <TextField
                  fullWidth
                  label="Agent Name"
                  name="locname"
                  value={formData?.locname}
                  onChange={(e) => handleOnChange(e, formData, setFormData)}
                  required
                />
              </Col>
              <Col item xs={6}>
                <TextField fullWidth value={formData?.phone} name="phone" onChange={(e) => handleOnChange(e, formData, setFormData)} label="Phone Number" />
              </Col>
              <Col item xs={6}>
                <FormControl fullWidth label="Agency Type" required>
                  <Select
                    id="agency-type-id"
                    name="agent_type"
                    label="Agency Type"
                    value={formData?.agent_type}
                    onChange={(e) => handleOnChange(e, formData, setFormData)}
                    menuItems={AGENCY_TYPE}></Select>
                </FormControl>
              </Col>
              <Col item xs={6}>
                <TextField fullWidth label="FAX Number" name="fax" value={formData?.fax} onChange={(e) => handleOnChange(e, formData, setFormData)} />
              </Col>
              <Col item xs={6}>
                <TextField
                  fullWidth
                  data-testid="address-id"
                  label="Address"
                  name="address"
                  value={formData?.address}
                  onChange={(e) => handleOnChange(e, formData, setFormData)}
                  required
                />
              </Col>
              <Col item xs={6}>
                <TextField
                  fullWidth
                  data-testid="email-id"
                  name="email"
                  label="Email Address"
                  /*
                  set email to null if it is empty to ensure it passes validation in aoc-api
                  aoc-api looks for email string in the payload
                  */
                  value={formData?.email === '' ? setFormData({ ...formData, email: null }) : formData?.email}
                  onChange={(e) => handleOnChange(e, formData, setFormData)}></TextField>
              </Col>
              <Col item xs={6}>
                <TextField
                  fullWidth
                  data-testid="city-id"
                  name="city"
                  value={formData?.city}
                  label="City"
                  onChange={(e) => handleOnChange(e, formData, setFormData)}
                  required></TextField>
              </Col>
              <Col item xs={6}>
                <TextField
                  fullWidth
                  data-testid="website-id"
                  name="web"
                  value={formData?.web}
                  label="Website"
                  onChange={(e) => handleOnChange(e, formData, setFormData)}></TextField>
              </Col>
              <Col item xs={6}>
                <TextField
                  fullWidth
                  data-testid="county-id"
                  name="county"
                  value={formData?.county}
                  label="County"
                  onChange={(e) => handleOnChange(e, formData, setFormData)}></TextField>
              </Col>
              <Col item xs={6}>
                <TextField
                  fullWidth
                  data-testid="lat-id"
                  name="lat"
                  value={formData?.lat}
                  onChange={(e) => handleOnChange(e, formData, setFormData)}
                  label="Latitude"
                  required></TextField>
              </Col>
              <Col item xs={6}>
                <FormControl fullWidth label="Agency Type" required>
                  <SelectStates
                    label="States"
                    name="state"
                    value={formData?.state}
                    onChange={(e) => handleOnChange(e, formData, setFormData)}
                    includeAllOption={true}
                    fullWidth={true}
                  />
                </FormControl>
              </Col>
              <Col item xs={6}>
                <TextField
                  fullWidth
                  data-testid="lng-id"
                  label="Longitude"
                  name="lng"
                  onChange={(e) => handleOnChange(e, formData, setFormData)}
                  value={formData?.lng}
                  required></TextField>
              </Col>
              <Col item xs={6} />
              <Col item xs={6}>
                <Typography>
                  Use{' '}
                  <Link variant="secondary" href="https://www.latlong.net" target="_blank" rel="noopener noreferrer" endIcon={'openInNew'}>
                    LatLong.net
                  </Link>{' '}
                  to find agents latitude and longitude.
                </Typography>
                <Typography variant="secondary"> * Indicates a required field.</Typography>
              </Col>
              <Col item xs={6} />
              <Col item xs={6}>
                <Col container justifyContent="flex-end">
                  <Button
                    color="primary"
                    data-testid="submit-id"
                    loading={loading}
                    className={classes.button}
                    onClick={handleSubmitClick}
                    disabled={
                      !formData?.lat ||
                      !formData?.lng ||
                      !formData?.state ||
                      !formData?.agent_type ||
                      !formData?.locname ||
                      !formData?.code ||
                      !formData?.postal ||
                      !formData?.city ||
                      !formData?.address ||
                      loading
                    }>
                    Submit
                    {loading && !isModalOpen && <CircularProgress size={24} className={classes.buttonProgress} />}
                  </Button>
                  <Button color="secondary" variant="outlined" onClick={handleClearClick} className={classes.button}>
                    Clear
                  </Button>
                  {agent?.code && (
                    <Button color="secondary" variant="outlined" className={classes.button} onClick={handleDeleteClick}>
                      Delete
                    </Button>
                  )}
                </Col>
              </Col>
            </Row>
          </Paper>
          <Modal
            open={isModalOpen}
            onClose={handleClose}
            aria-labelledby="delete-agent-modal"
            aria-describedby="get confirmation from admin regarding the deletion of the agent"
            className={classes.modal}>
            <Paper style={{ padding: `1rem` }}>
              <Row container direction="row" justifyContent="center" alignItems="center" spacing={2}>
                <Col item xs={12}>
                  <Typography id="modal-title" variant="body1" color="textSecondary" align="center">
                    Are you sure you want to delete this agent?
                  </Typography>
                </Col>
                <Col item xs={12} align="center">
                  <Button color="primary" onClick={handleOkClick} className={classes.button} disabled={loading}>
                    ok
                    {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
                  </Button>
                  <Button color="secondary" variant="outlined" onClick={handleClose} className={classes.button}>
                    cancel
                  </Button>
                </Col>
              </Row>
            </Paper>
          </Modal>
        </Box>
      </WorkflowViewContainer>
    </Feature>
  );
};

AddOrUpdateAgent.propTypes = {
  triggerButton: PropTypes.func,
  agent: PropTypes.object,
  resetSearchResults: PropTypes.func,
};

export default AddOrUpdateAgent;
